import { useTranslation } from "react-i18next"

import { Badge } from "@/components/ui/badge"
import { cn } from "@/lib/utils"

/**
 * OKS-49 — Per-row rank chip on the brand crew roster (UX Spec §2.2).
 *
 * Inserted as the rightmost chip in the metric strip on
 * `<CrewListRow>` — sibling of the OKS-48 `<CrewListXpChip>`. Reads
 * `member.rank.name` (BE serialiser key — see
 * `CrewMembershipsController#serialise_row`).
 *
 * Format: `Rising Star` (variant `secondary` — distinct from the XP
 * chip's neutral background). Truncates with ellipsis on narrow
 * cards; the `aria-label` carries the full name for screen readers.
 *
 * # Locked testid
 *
 *   - `crew-rank-chip` — chip wrapper, indexed by the parent row's
 *     `data-member-id` for QA selectors.
 */
interface CrewRankChipProps {
  name: string
  className?: string
}

export function CrewRankChip({ name, className }: CrewRankChipProps) {
  const { t } = useTranslation()

  return (
    <Badge
      variant="secondary"
      data-testid="crew-rank-chip"
      data-rank-name={name}
      aria-label={t("rank.chip.aria_label", { name })}
      className={cn("max-w-full truncate", className)}
    >
      {name}
    </Badge>
  )
}
